import yaml from "js-yaml";
import type { Meta, Owner } from "./types";

interface FrontmatterResult {
  meta: Meta;
  body: string;
}

function normalizeOwners(raw: unknown): Owner[] | undefined {
  if (raw === undefined || raw === null) return undefined;
  if (typeof raw === "string") return [{ name: raw }];
  if (Array.isArray(raw)) {
    return raw
      .map((entry): Owner | null => {
        if (typeof entry === "string") return { name: entry };
        if (entry && typeof entry === "object") {
          const rec = entry as Record<string, unknown>;
          const name = typeof rec.name === "string" ? rec.name : undefined;
          if (!name) return null;
          const contact = typeof rec.contact === "string" ? rec.contact : undefined;
          return contact ? { name, contact } : { name };
        }
        return null;
      })
      .filter((v): v is Owner => v !== null);
  }
  if (typeof raw === "object") {
    const rec = raw as Record<string, unknown>;
    const name = typeof rec.name === "string" ? rec.name : undefined;
    if (!name) return undefined;
    const contact = typeof rec.contact === "string" ? rec.contact : undefined;
    return contact ? [{ name, contact }] : [{ name }];
  }
  return undefined;
}

const RUNTIME_CONTEXT_MAX = 40;

function validateRuntimeContext(raw: unknown): string | undefined {
  if (typeof raw !== "string") return undefined;
  if (raw.length > RUNTIME_CONTEXT_MAX) {
    console.warn(`[arch-parser] runtime_context exceeds ${RUNTIME_CONTEXT_MAX} chars; dropped`);
    return undefined;
  }
  return raw;
}

function normalizeClusters(raw: unknown): Record<string, { runtime_context?: string }> | undefined {
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) return undefined;
  const out: Record<string, { runtime_context?: string }> = {};
  for (const [key, value] of Object.entries(raw as Record<string, unknown>)) {
    if (!value || typeof value !== "object") continue;
    const entry = value as Record<string, unknown>;
    out[key] = { runtime_context: validateRuntimeContext(entry.runtime_context) };
  }
  return Object.keys(out).length > 0 ? out : undefined;
}

export function parseFrontmatter(markdown: string): FrontmatterResult {
  const lines = markdown.split("\n");
  if (lines[0]?.trim() !== "---") {
    return { meta: { name: "Untitled" }, body: markdown };
  }

  let endIdx = -1;
  for (let i = 1; i < lines.length; i += 1) {
    if (lines[i]?.trim() === "---") {
      endIdx = i;
      break;
    }
  }
  if (endIdx === -1) {
    return { meta: { name: "Untitled" }, body: markdown };
  }

  const fmText = lines.slice(1, endIdx).join("\n");
  const body = lines.slice(endIdx + 1).join("\n");

  let raw: unknown;
  try {
    raw = yaml.load(fmText);
  } catch (err) {
    console.warn("[arch-parser] frontmatter YAML parse failed:", err);
    return { meta: { name: "Untitled" }, body };
  }

  if (!raw || typeof raw !== "object") {
    return { meta: { name: "Untitled" }, body };
  }

  const rec = raw as Record<string, unknown>;
  const meta: Meta = {
    name: typeof rec.name === "string" ? rec.name : "Untitled",
    schema: typeof rec.schema === "string" ? rec.schema : undefined,
    description: typeof rec.description === "string" ? rec.description : undefined,
    version: typeof rec.version === "string" ? rec.version : undefined,
    date: typeof rec.date === "string" ? rec.date : undefined,
    repo: typeof rec.repo === "string" ? rec.repo : undefined,
    generated_by: typeof rec.generated_by === "string" ? rec.generated_by : undefined,
    tags: Array.isArray(rec.tags)
      ? rec.tags.filter((t): t is string => typeof t === "string")
      : undefined,
    owners: normalizeOwners(rec.owners),
    environments: Array.isArray(rec.environments)
      ? (rec.environments.filter((e) => e && typeof e === "object") as Meta["environments"])
      : undefined,
    runtime_context: validateRuntimeContext(rec.runtime_context),
    clusters: normalizeClusters(rec.clusters),
  };

  for (const [key, value] of Object.entries(rec)) {
    // Validated fields above already own these keys — skip so the
    // catch-all doesn't restore the raw, unvalidated value.
    if (key === "runtime_context" || key === "clusters") continue;
    if (!(key in meta)) meta[key] = value;
  }

  return { meta, body };
}
